import React, { useState } from "react";
import PropTypes from "prop-types";
import { Header } from "components/DisplayBox";
import { FormattedMessage } from "react-intl";
import { shortenText } from "utils";
import styled from "@emotion/styled";
import { colors } from "style";
import { GET_CHALLENGES } from "graphql/queries";
import { CHALLENGES, MISSION_ROLE_STATES } from "constants/index";
import { useAuth, useMissionRole } from "hooks";
import { CodeChallengeForm, CodeChallengeItemsModal, CodeChallengesPicker } from "components/CodeChallenges";
import { Col, Row } from "components/Containers";
import { DataTableProvider } from "components/DataTable";
import { PAGINATION_TYPE } from "components/Pagination";
import Icon, { ICON_SIZE, ICON_TYPE } from "components/Icon";
import Tags, { TAG_SIZE } from "components/Tags";
import { TAG_COLORS } from "components/Tags/Tag";
import { H3 } from "components/Text";
import { EditLink, PrimaryLink } from "components/Links";
import { ActionButton, EditButton } from "components/Buttons";
import { Toggle } from "components/Form";
import { TOGGLE_SIZE } from "components/Form/Toggle";
import Tooltip from "components/Tooltip";
import { MessagesNoResults } from "components/Messages";

/**
 * RoleCodeChallenges
 *
 * @param {Array}    data
 * @param {Boolean}  dataTableView
 * @param {Boolean}  canEdit        Defaults to admin-only; pass it where another role may pick the challenges
 * @param {Array}    refetchQueries Queries to re-read after the challenges change, for a caller the cache cannot update
 */
const RoleCodeChallenges = ({ data, dataTableView = false, canEdit: canManage, refetchQueries = [], ...props }) => {
  const { isAdmin, isClient } = useAuth();
  const canManageChallenges = canManage ?? isAdmin;
  const [selectedChallenge, setSelectedChallenge] = useState();
  const [showForm, setShowForm] = useState(false);
  const [challengeDetails, setChallengeDetails] = useState();
  const [challengeIds, setChallengeIds] = useState([]);
  const challengesExists = !!data?.challenges?.length;

  const { handleUpdateMissionRoleCodeChallenges, handleToggleMissionRoleChallenge } = useMissionRole({
    skip: true,
    includeDisabledChallenges: isAdmin,
  });

  const rowOptions = ({ id }) => {
    const missionRoleChallenge = data?.missionRoleChallenges?.find(({ challenge }) => challenge?.id === id);
    const hasResponses = missionRoleChallenge?.responsesCount > 0;

    return [
      {
        label: hasResponses ? (
          <Tooltip tooltipId="RoleCodeChallenges.ChallengeHasResponsesTooltip">
            <FormattedMessage id="Global.Remove" />
          </Tooltip>
        ) : (
          <FormattedMessage id="Global.Remove" />
        ),
        icon: ICON_TYPE.trash,
        disabled: data?.state === MISSION_ROLE_STATES.closed || hasResponses,
        onClick: () =>
          handleUpdateMissionRoleCodeChallenges({
            id: data?.id,
            params: {
              challenges: data?.challenges
                .filter(({ id: challengeId }) => challengeId !== id)
                .map(({ id }) => ({ id })),
            },
            refetchQueries,
          }),
      },
    ];
  };

  return (
    <>
      <Header {...props}>
        <H3>
          <FormattedMessage id="Missions.MissionRole.CodeChallenges" />
        </H3>
        {challengesExists && canManageChallenges && (
          <EditLink onClick={() => setShowForm(true)}>
            <FormattedMessage id="RoleCodeChallenges.EditButtonText" />
          </EditLink>
        )}
      </Header>
      {challengesExists ? (
        canManageChallenges || dataTableView ? (
          <DataTableProvider
            keyName="challenges"
            queryName={GET_CHALLENGES}
            variables={{
              filters: {
                ids: data?.challenges.map(({ id }) => id),
              },
            }}
            columnData={COLUMNS(
              setSelectedChallenge,
              handleToggleMissionRoleChallenge,
              isAdmin,
              data,
              setChallengeDetails,
              isClient
            )}
            rowOptions={canManageChallenges ? rowOptions : null}
            paginationType={PAGINATION_TYPE.cursor}
            paginationProps={{
              countMessage: "Global.CodeChallenges.Count",
            }}
            disablePagination
          />
        ) : (
          <Tags data={data?.challenges} />
        )
      ) : canManageChallenges ? (
        <EditButton onClick={() => setShowForm(true)}>
          <FormattedMessage id="RoleCodeChallenges.EditButtonText" />
        </EditButton>
      ) : (
        <MessagesNoResults title="RoleCodeChallenges.EmptyMsg" />
      )}
      {isAdmin && selectedChallenge && (
        <CodeChallengeForm
          show={selectedChallenge}
          onClose={() => setSelectedChallenge()}
          challengeId={selectedChallenge?.id}
          data={selectedChallenge}
          isEditing={true}
        />
      )}
      {canManageChallenges && showForm && (
        <CodeChallengesPicker
          show={showForm}
          onClose={() => setShowForm(false)}
          title={<FormattedMessage id="RoleCodeChallenges.CodeChallengesPicker.Title" />}
          info={<FormattedMessage id="RoleCodeChallenges.CodeChallengesPicker.Description" />}
          onSelect={setChallengeIds}
          onSubmit={() => {
            const existingChallenges = data?.challenges?.map((challenge) => challenge.id) || [];
            const allChallenges = [...new Set([...existingChallenges, ...challengeIds])];
            handleUpdateMissionRoleCodeChallenges({
              id: data?.id,
              params: { challenges: allChallenges.map((id) => ({ id })) },
              refetchQueries,
            });
            setShowForm(false);
          }}
          selectionInitialState={data?.challenges.map(({ id }) => id)}
          showSave
          selectable
          canSubmit
        />
      )}

      <CodeChallengeItemsModal
        challengeDetails={challengeDetails}
        show={!!challengeDetails}
        onClose={() => setChallengeDetails()}
      />
    </>
  );
};

const COLUMNS = (onClick, onToggleChallenge, isAdmin, data, setChallengeDetails, isClient) =>
  [
    isAdmin && {
      cell: (row) => {
        const missionRoleChallenge = data?.missionRoleChallenges?.find(({ challenge }) => challenge?.id === row?.id);

        return (
          <Toggle
            name={`challenge-${row.id}`}
            value={missionRoleChallenge?.enabled}
            onChange={() =>
              onToggleChallenge?.({ id: missionRoleChallenge?.id, enabled: !missionRoleChallenge?.enabled })
            }
            disabled={data.state === MISSION_ROLE_STATES.closed}
            size={TOGGLE_SIZE.small}
          />
        );
      },
      width: "60px",
    },
    {
      label: <FormattedMessage id={`CodeChallenges.CodeChallengeList.${isAdmin ? "" : "User"}ColumnTitle`} />,
      cell: (row) => (
        <Row>
          <div>
            <Icon type={ICON_TYPE[row?.icon || `data`]} size={ICON_SIZE.large} color="inherit" />
          </div>
          <Col gap="0.25rem">
            <PrimaryLink onClick={() => onClick(row)}>
              <Title>{shortenText(row?.name, 40)}</Title>
            </PrimaryLink>
            <Type>{CHALLENGES[row?.type]}</Type>
            <Id>{row?.serviceId}</Id>
          </Col>
        </Row>
      ),
      width: "30%",
    },
    isAdmin && {
      label: <FormattedMessage id="CodeChallenges.CodeChallengeList.ColumnPrivateTitle" />,
      cell: (row) => row?.privateName,
      width: "15%",
    },
    {
      label: <FormattedMessage id="CodeChallenges.CodeChallengeList.ColumnSkills" />,
      cell: (row) => (
        <Row gap="0.4rem">
          {row?.skills?.length > 0 && (
            <Tags data={row?.skills} limit={2} unwrap size={TAG_SIZE.small} {...TAG_COLORS.pink} />
          )}
          {row?.disciplines?.length > 0 && (
            <Tags data={row?.disciplines} limit={2} unwrap size={TAG_SIZE.small} {...TAG_COLORS.purple} />
          )}
        </Row>
      ),
      width: "25%",
    },
    isAdmin && {
      label: <FormattedMessage id="CodeChallenges.CodeChallengeList.ColumnCategory" />,
      cell: (row) => row?.types?.length > 0 && <Tags data={row?.types} limit={2} />,
      width: "15%",
    },
    (isAdmin || isClient) && {
      cell: (row) => (
        <ActionButton onClick={() => setChallengeDetails(row)}>
          <Icon type={ICON_TYPE.eye} size={ICON_SIZE.large} color={colors.purpleRainBase} />
        </ActionButton>
      ),
      width: "10%",
    },
  ].filter(Boolean);

const Type = styled.span`
  font-weight: 500;
`;

const Title = styled.span`
  font-weight: 500;
`;

const Id = styled.span`
  font-weight: 400;
  color: ${colors.grayAnatomyLight1};
`;

RoleCodeChallenges.propTypes = {
  data: PropTypes.object,
  dataTableView: PropTypes.bool,
  canEdit: PropTypes.bool,
  refetchQueries: PropTypes.array,
};

export default RoleCodeChallenges;
